import { useCallback } from 'react';
import { ApolloError, useApolloClient } from '@apollo/client';

import {
  GroupInvoiceIssuePreflightQuery,
  InvoiceIssueJobProgressQuery,
  useEnqueueBulkInvoiceIssueMutation,
  useGroupInvoiceIssuePreflightQuery,
  useInvoiceIssueJobProgressQuery,
} from 'core/graphql';

import { evictInvoiceIssueLists } from './evictFinanceLists';
import type { IDocumentKind } from './useIssueFaktura';
import { isPermissionDeniedError } from './errors';

/** The per-group preflight envelope (`total`/`billableCount`/`rows`). */
export type IBulkBillingPreview = NonNullable<
  GroupInvoiceIssuePreflightQuery['fakturowniaGroupBillingPreview']
>;
/** One preview row — the SAME shape the single-student preflight returns. */
export type IBulkBillingRow = IBulkBillingPreview['rows'][number];
/** The background run's live progress row. */
export type IBulkBillingJob = NonNullable<
  InvoiceIssueJobProgressQuery['fakturowniaBillingJob']
>;
/** One result row (issued / skipped / failed / pending). */
export type IBulkBillingJobRow = NonNullable<IBulkBillingJob['rows']>[number];

/** Normalized outcome of one enqueue attempt. */
export interface IBulkEnqueueResult {
  ok: boolean;
  /** The job's global id to poll with (`InvoiceIssueJobProgress`). */
  jobId: string | null;
  /** Machine reason code on failure (`errorCode`), `null` on success. */
  errorCode: string | null;
  /** Backend messages, already filtered of nullish entries. */
  errors: string[];
  /** Server's own recount, echoed back — used to reconcile a stale drawer. */
  billableCount: number | null;
  skippedCount: number | null;
}

interface IEnqueueArgs {
  /** Relay ids of the agreements to bill — the operator's ticked rows. */
  agreementIds: string[];
  /** The billable count the drawer showed; the server refuses on mismatch. */
  expectedBillableCount: number;
  /** Optional override of the invoices' payment-due date. */
  paymentTo?: string | null;
}

interface IUseBulkIssueFakturaArgs {
  /** Relay global id of the group. */
  groupId: string;
  /** The drawer is open — gates the (whole-group) preflight query. */
  visible: boolean;
  /** The enqueued job's id, or `null` before a run has started. */
  jobId: string | null;
  /**
   * Keep polling the job. The owner passes `false` once it has seen a terminal
   * status, so exactly ONE render happens after terminal — polling driven by
   * an inline `job.status` check would fire one extra request past the end.
   */
  polling: boolean;
  /**
   * ТЗ 0101: what the whole run issues. The preview and the enqueue get the
   * SAME value — a VAT preview must never be confirmed into a proforma run.
   */
  kind?: IDocumentKind;
}

/** The hook's surface — the drawer owns selection + lifecycle, this owns data. */
export interface IUseBulkIssueFakturaApi {
  preview: IBulkBillingPreview | null;
  rows: IBulkBillingRow[];
  previewLoading: boolean;
  previewError?: ApolloError;
  permissionDenied: boolean;
  refetchPreview: () => void;
  enqueue: (args: IEnqueueArgs) => Promise<IBulkEnqueueResult>;
  enqueuing: boolean;
  job: IBulkBillingJob | null;
  jobRows: IBulkBillingJobRow[];
  /** Evict the finance list drains + bump the version — call ONCE on terminal. */
  evictInvoiceLists: () => void;
}

const NO_ROWS: IBulkBillingRow[] = [];
const NO_JOB_ROWS: IBulkBillingJobRow[] = [];

/**
 * BULK «Выставить фактуры» — issue REAL Fakturownia VAT documents for every
 * billable взнос of a group in one background run.
 *
 * The data seam for `BulkIssueFakturaDrawer`. It wraps the three generated
 * Apollo hooks so no raw generated hook leaks into the component, and it keeps
 * the fiscal-safety contract in one place:
 *   - the PREVIEW (`fakturowniaGroupBillingPreview`) runs the same
 *     `check_billability()` the run runs, per agreement, so the drawer previews
 *     exactly what will be POSTed and can render «пропущено» rows;
 *   - the ENQUEUE (`issueInvoicesForGroup`) returns immediately with a `job`;
 *     it carries `expectedBillableCount` so the SERVER recounts and refuses
 *     (`BILLABLE_COUNT_CHANGED`) if the billable set moved under the operator;
 *   - the POLL (`fakturowniaBillingJob`) is `network-only` and stops the moment
 *     the owner flips `polling` false on a terminal status.
 *
 * Cache: NOTHING is evicted at enqueue — no document exists yet. `evictInvoiceLists`
 * is exposed so the drawer can evict ONCE when the job reaches a terminal state,
 * which is the only moment real documents exist.
 */
export const useBulkIssueFaktura = ({
  groupId,
  visible,
  jobId,
  polling,
  kind = 'vat',
}: IUseBulkIssueFakturaArgs): IUseBulkIssueFakturaApi => {
  const client = useApolloClient();

  const {
    data: previewData,
    loading: previewLoading,
    error: previewError,
    refetch,
  } = useGroupInvoiceIssuePreflightQuery({
    variables: { groupId, kind },
    skip: !visible || !groupId,
    // cache-and-network so a re-open repaints instantly, then always refetches
    // fresh ALREADY_INVOICED verdicts; the server's own recount on enqueue is
    // the true guard against acting on a momentarily-stale count.
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  const [enqueueMutation, enqueueState] = useEnqueueBulkInvoiceIssueMutation();

  const { data: jobData } = useInvoiceIssueJobProgressQuery({
    variables: { id: jobId || '' },
    skip: !jobId,
    fetchPolicy: 'network-only',
    pollInterval: polling ? 1500 : 0,
  });

  const enqueue = useCallback(
    async ({
      agreementIds,
      expectedBillableCount,
      paymentTo,
    }: IEnqueueArgs): Promise<IBulkEnqueueResult> => {
      try {
        const result = await enqueueMutation({
          variables: {
            groupId,
            agreementIds,
            expectedBillableCount,
            paymentTo: paymentTo || undefined,
            kind,
          },
        });
        const payload = result.data?.issueInvoicesForGroup;
        const errors = (payload?.errors || []).filter((e): e is string =>
          Boolean(e),
        );
        return {
          ok: Boolean(payload?.success),
          jobId: payload?.success ? payload.job?.id || null : null,
          errorCode: payload?.success ? null : payload?.errorCode || null,
          errors,
          billableCount: payload?.billableCount ?? null,
          skippedCount: payload?.skippedCount ?? null,
        };
      } catch (error) {
        return {
          ok: false,
          jobId: null,
          errorCode: 'ENQUEUE_FAILED',
          errors: [(error as ApolloError).message],
          billableCount: null,
          skippedCount: null,
        };
      }
    },
    [enqueueMutation, groupId, kind],
  );

  const evictInvoiceLists = useCallback(() => {
    evictInvoiceIssueLists(client.cache);
  }, [client]);

  const preview = previewData?.fakturowniaGroupBillingPreview || null;
  const job = jobData?.fakturowniaBillingJob || null;

  return {
    preview,
    rows: preview?.rows || NO_ROWS,
    previewLoading,
    previewError,
    permissionDenied: isPermissionDeniedError(previewError),
    refetchPreview: () => {
      refetch();
    },
    enqueue,
    enqueuing: enqueueState.loading,
    job,
    jobRows: job?.rows || NO_JOB_ROWS,
    evictInvoiceLists,
  };
};
